import { arrayMove } from "@dnd-kit/sortable";

import type { GetLegalSubpoenaExhibitsResponse } from "@upclaim/sdk/src/data-contracts";

export type Exhibit = GetLegalSubpoenaExhibitsResponse["exhibits"][number];

/**
 * A claim document that could join the bundle, as the API describes it.
 *
 * `defaultLabel` comes from the server so the bordereau wording lives in one place —
 * duplicating the type→label map here would drift the first time the legal team
 * corrects a string.
 */
export type ClaimDocumentOption = GetLegalSubpoenaExhibitsResponse["claimDocuments"][number];

/** "7" / "7.1" — how the piece is printed in the bordereau. */
export function printedNumber(exhibit: Pick<Exhibit, "exhibitNumber" | "exhibitSubNumber">): string {
  return exhibit.exhibitSubNumber == null
    ? `${exhibit.exhibitNumber}`
    : `${exhibit.exhibitNumber}.${exhibit.exhibitSubNumber}`;
}

/**
 * Renumber the list contiguously from its current order: each plain piece takes the
 * next number, each sub-piece follows the piece above it. This is the "tidy up"
 * a jurist wants after dragging things around.
 */
export function renumberSequentially(exhibits: Exhibit[]): Exhibit[] {
  let currentNumber = 0;
  let currentSub = 0;

  return exhibits.map((exhibit) => {
    if (exhibit.exhibitSubNumber == null) {
      currentNumber += 1;
      currentSub = 0;
      return { ...exhibit, exhibitNumber: currentNumber };
    }

    // A sub-piece leading the list has no parent to hang off — promote it.
    if (currentNumber === 0) {
      currentNumber += 1;
      return { ...exhibit, exhibitNumber: currentNumber, exhibitSubNumber: null };
    }

    currentSub += 1;
    return { ...exhibit, exhibitNumber: currentNumber, exhibitSubNumber: currentSub };
  });
}

/** Numbers printed more than once — a filing error nobody catches by eye. */
export function findDuplicates(exhibits: Exhibit[]): string[] {
  const counts = new Map<string, number>();

  for (const exhibit of exhibits) {
    const printed = printedNumber(exhibit);
    counts.set(printed, (counts.get(printed) || 0) + 1);
  }

  return [...counts.entries()].filter(([, count]) => count > 1).map(([printed]) => printed);
}

/** Gaps in the main numbering — "Pièce n°5" missing between 4 and 6. */
export function findGaps(exhibits: Exhibit[]): number[] {
  const numbers = new Set(exhibits.map((exhibit) => exhibit.exhibitNumber));
  const max = Math.max(0, ...numbers);

  return Array.from({ length: max }, (_, index) => index + 1).filter((number) => !numbers.has(number));
}

/**
 * Turn a claim document into an exhibit at the end of the numbering.
 *
 * It takes the next free number rather than the number of the slot it was dropped
 * into: the subpoena's prose cites pieces by number, so adding a document must not
 * silently shift what "(Pièce n°8)" refers to. "Renuméroter" re-sequences on demand.
 */
export function toExhibit(document: ClaimDocumentOption, exhibits: Exhibit[]): Exhibit {
  const nextNumber = Math.max(0, ...exhibits.map((exhibit) => exhibit.exhibitNumber)) + 1;

  return {
    claimDocumentId: document.claimDocumentId,
    order: exhibits.length,
    exhibitNumber: nextNumber,
    exhibitSubNumber: null,
    // The wording the API says this type carries — shown straight away and editable,
    // which is what makes an "OTHER" document nameable. Never the filename: that is
    // what used to end up printed in a filed assignation.
    label: document.defaultLabel,
    name: document.name,
    documentType: document.documentType,
  };
}

/** Re-index `order` so it matches the list's position. */
export function withSequentialOrder(exhibits: Exhibit[]): Exhibit[] {
  return exhibits.map((exhibit, index) => ({ ...exhibit, order: index }));
}

/** How far right (or back left) a row has to be dragged to change level. */
export const NEST_OFFSET_PX = 24;

/**
 * Which way the pointer has moved sideways since the drag started: `1` right enough to
 * nest, `-1` left enough to un-nest, `0` neither — the row keeps its level.
 */
export type NestIntent = -1 | 0 | 1;

export function nestIntentOf(offsetX: number): NestIntent {
  if (offsetX >= NEST_OFFSET_PX) {
    return 1;
  }
  return offsetX <= -NEST_OFFSET_PX ? -1 : 0;
}

/** The sub-pieces directly under a plain piece — none for a sub-piece. */
export function subPiecesOf(exhibits: Exhibit[], claimDocumentId: string): Exhibit[] {
  const index = exhibits.findIndex((exhibit) => exhibit.claimDocumentId === claimDocumentId);

  if (index === -1 || exhibits[index].exhibitSubNumber != null) {
    return [];
  }

  const subPieces: Exhibit[] = [];
  for (let next = index + 1; next < exhibits.length && exhibits[next].exhibitSubNumber != null; next++) {
    subPieces.push(exhibits[next]);
  }
  return subPieces;
}

/**
 * Where a dragged row lands, and at which level.
 *
 * Drives both the live preview (numbers and indentation while dragging) and the drop
 * itself, so what the jurist sees before letting go is what they get. The level
 * follows the pointer's sideways movement, the way outliners nest items:
 *
 * - dragged right, the row becomes a sub-piece of the piece above it;
 * - dragged left, it becomes a plain piece again;
 * - otherwise it keeps the level it had.
 *
 * A plain piece carries its sub-pieces with it — they used to stay behind and quietly
 * re-attach to whichever piece ended up above them. Two constraints keep the result
 * well-formed: the first row, and a piece that has sub-pieces, cannot become a
 * sub-piece; and a row dropped just before someone else's sub-piece joins that piece
 * as a sub-piece too, rather than taking the sub-piece over.
 *
 * Numbers are left to {@link renumberSequentially}; only the level is set here.
 */
export function projectDrop(
  exhibits: Exhibit[],
  activeId: string,
  overId: string | null,
  intent: NestIntent,
): { exhibits: Exhibit[]; isSubPiece: boolean } {
  const subPieces = subPiecesOf(exhibits, activeId);
  const subPieceIds = new Set(subPieces.map((exhibit) => exhibit.claimDocumentId));
  const visible = exhibits.filter((exhibit) => !subPieceIds.has(exhibit.claimDocumentId));

  const activeIndex = visible.findIndex((exhibit) => exhibit.claimDocumentId === activeId);
  if (activeIndex === -1) {
    return { exhibits, isSubPiece: false };
  }

  const overIndex = overId ? visible.findIndex((exhibit) => exhibit.claimDocumentId === overId) : -1;
  const moved = arrayMove(visible, activeIndex, overIndex === -1 ? activeIndex : overIndex);
  const index = moved.findIndex((exhibit) => exhibit.claimDocumentId === activeId);

  const wasSubPiece = moved[index].exhibitSubNumber != null;
  const wantsSubPiece = intent === 0 ? wasSubPiece : intent === 1;
  const canBeSubPiece = index > 0 && subPieces.length === 0;
  const mustBeSubPiece = moved[index + 1]?.exhibitSubNumber != null;
  const isSubPiece = canBeSubPiece && (wantsSubPiece || mustBeSubPiece);

  moved[index] = { ...moved[index], exhibitSubNumber: isSubPiece ? 1 : null };
  moved.splice(index + 1, 0, ...subPieces);

  return { exhibits: moved, isSubPiece };
}
